import { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MODULE_CATALOG } from '@vce/shared';
import { useAuth } from '@/app/auth-context';
import { cn } from '@/lib/utils';

const CONTACT_MAILTO =
  'mailto:ecarlosvao@gmail.com?subject=Demonstra%C3%A7%C3%A3o%20catalyze.ai%20%E2%80%94%20cat%C3%A1logo%20VTEX';

function truncateEmail(email: string, max = 20): string {
  if (email.length <= max) return email;
  return `${email.slice(0, max - 3)}...`;
}

function ChatIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function UserMenu({ email }: { email: string }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = useCallback(() => {
    void logout().then(() => navigate('/login'));
  }, [logout, navigate]);

  const itemClass = cn(
    'block w-full px-3.5 py-2 text-left text-sm font-medium text-[var(--color-body)]',
    'transition-colors hover:text-[var(--color-ink)]',
    'focus-visible:outline-none focus-visible:text-[var(--color-ink)]',
  );

  return (
    <div
      className="relative inline-block"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocusCapture={() => setOpen(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setOpen(false);
        }
      }}
    >
      <button
        type="button"
        className={cn(
          'whitespace-nowrap text-sm font-medium tracking-tight text-[var(--color-body)] transition-colors',
          'hover:text-[var(--color-ink)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary)]',
          open && 'text-[var(--color-ink)]',
        )}
        aria-expanded={open}
        aria-haspopup="menu"
        title={email}
      >
        bem vindo, {truncateEmail(email)}
      </button>

      <div
        role="menu"
        className={cn(
          'absolute left-0 top-full z-50 w-full',
          'border-x border-b border-[var(--color-hairline)] bg-[var(--color-surface-card)] py-1',
          'rounded-b-[8px]',
          open ? 'visible' : 'invisible',
        )}
      >
        <Link
          role="menuitem"
          to={MODULE_CATALOG.dashboard.path}
          className={itemClass}
          onClick={() => setOpen(false)}
        >
          {MODULE_CATALOG.dashboard.label}
        </Link>
        <Link role="menuitem" to="/settings" className={itemClass} onClick={() => setOpen(false)}>
          Configurações
        </Link>
        <div className="my-1 h-px bg-[var(--color-hairline)]" />
        <button type="button" role="menuitem" onClick={handleLogout} className={itemClass}>
          Sair
        </button>
      </div>
    </div>
  );
}

export function LandingPage() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-[var(--color-canvas)] text-[var(--color-ink)]">
      <div className="mx-auto flex min-h-screen w-full max-w-[1280px] flex-col px-6 sm:px-10">
        <header className="flex h-16 shrink-0 items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 transition-opacity hover:opacity-90">
            <img src="/favicon.svg" alt="" width={28} height={28} className="rounded-lg" />
            <span className="font-display text-lg tracking-tight text-[var(--color-ink)]">
              catalyze.ai
            </span>
          </Link>

          {user ? (
            <UserMenu email={user.email} />
          ) : (
            <Link
              to="/login"
              className="text-sm font-medium text-[var(--color-body)] transition-colors hover:text-[var(--color-ink)]"
            >
              Login
            </Link>
          )}
        </header>

        <main className="flex flex-1 flex-col items-center justify-center pb-20 pt-10 text-center sm:pb-24 sm:pt-14">
          <h1
            className={cn(
              'max-w-3xl text-[32px] font-bold leading-[1.15] tracking-[-1px] text-[var(--color-ink)]',
              'sm:text-[56px] sm:leading-[1.1] sm:tracking-[-2px]',
              'animate-[landing-fade-up_0.7s_ease-out_both]',
            )}
          >
            Enriqueça, aprove,{' '}
            <span className="text-[var(--color-primary)]">publique.</span>
          </h1>

          <p
            className={cn(
              'mt-5 max-w-xl text-base leading-[1.55] text-[var(--color-body)]',
              'animate-[landing-fade-up_0.7s_ease-out_0.16s_both]',
            )}
          >
            Auditoria e enriquecimento de catálogo com IA — você revisa e só então publica na
            VTEX.
          </p>

          <video
            className={cn(
              'mt-9 aspect-video w-full max-w-3xl rounded-[12px] object-cover',
              'border border-[var(--color-hairline)] bg-[var(--color-canvas)]',
              'shadow-[0_0_60px_-20px_rgba(250,255,105,0.25)]',
              'animate-[landing-fade-up_0.7s_ease-out_0.2s_both]',
            )}
            src="/videos/catalyze-intro.mp4"
            poster="/videos/catalyze-intro-poster.jpg"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-label="Exemplos: auditar, gerar, aprovar e publicar no catalyze.ai"
          />

          <div className="mt-10 animate-[landing-fade-up_0.7s_ease-out_0.24s_both]">
            <a
              href={CONTACT_MAILTO}
              className={cn(
                'inline-flex h-11 items-center justify-center gap-2 rounded-[8px] px-6 text-sm font-semibold',
                'bg-[var(--color-primary)] text-[var(--color-on-primary)]',
                'transition hover:bg-[var(--color-primary-active)]',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary)]',
              )}
            >
              <ChatIcon />
              Agendar demonstração
            </a>
          </div>
        </main>

        <footer className="border-t border-[var(--color-hairline)] py-8">
          <p className="text-center text-xs text-[var(--color-muted)] sm:text-[13px]">
            <span className="font-display text-[var(--color-ink)]">catalyze.ai</span>
            {' — '}
            catálogo com revisão humana.
          </p>
        </footer>
      </div>
    </div>
  );
}
